/**
 * Shared slot-rendering helpers used across panels.
 * @module ui/helpers/slotRenderer
 */

const SLOT_DEFAULT_ICON = {
  helmet: 'helmet', chest: 'chest', gloves: 'gloves', boots: 'boots',
  weapon: 'sword', ring: 'ring', ring1: 'ring', ring2: 'ring', amulet: 'amulet',
};

function itemIconUrl(iconKey) {
  return `assets/sprites/items/icon_${iconKey}.png`;
}

function slotIconImg(slot, item, size = 28) {
  const key = item?.icon ?? SLOT_DEFAULT_ICON[slot] ?? 'sword';
  return `<img src="${itemIconUrl(key)}" width="${size}" height="${size}" style="image-rendering:pixelated;display:block" alt="${key}" onerror="this.onerror=null;this.src='${itemIconUrl('sword')}'">`;
}

/**
 * Render a hero's equipment slots in a container column.
 * @param {HTMLElement} col
 * @param {Array<[string, string, string]>} slots — [slotId, _emoji, label]
 * @param {object} hero — the hero whose `equipped` set is shown
 * @param {object} data
 * @param {(slot:string) => void} onUnequip
 */
export function renderEquipmentSlots(col, slots, hero, data, onUnequip) {
  col.innerHTML = '';
  for (const [slot, , label] of slots) {
    const item = hero.equipped[slot];
    const cell = document.createElement('button');
    cell.className = 'pd-slot';
    if (item) {
      cell.classList.add('filled');
      cell.dataset.rarity = item.rarity ?? 'common';
      cell.style.borderColor = data.rarity?.[item.rarity]?.color ?? '#9d9d9d';
      cell.innerHTML = `<span class="pd-slot-icon">${slotIconImg(slot, item)}</span>`;
      cell._pdItem = item;
      cell._pdAction = 'desequipar';
      cell.addEventListener('click', () => onUnequip(slot));
    } else {
      cell.classList.add('empty');
      cell.innerHTML = `<span class="pd-slot-icon empty">${slotIconImg(slot, null)}</span>`;
      cell.title = label;
    }
    col.appendChild(cell);
  }
}

/**
 * Render an inventory bag grid.
 * @param {HTMLElement} container
 * @param {object} state
 * @param {object} data
 * @param {(itemId:string) => void} onEquip
 * @param {object} [hero] the hero the bag is being viewed for (for class-lock)
 */
export function renderBagGrid(container, state, data, onEquip, hero) {
  const order = ['mythic', 'legendary', 'epic', 'rare', 'uncommon', 'common'];

  container.innerHTML = '';
  if (!state.inventory.length) {
    container.innerHTML = '<p class="pd-empty">Vazia. Derrote monstros para dropar itens.</p>';
    return;
  }

  const grid = document.createElement('div');
  grid.className = 'pd-bag-grid';

  const sorted = [...state.inventory].sort(
    (a, b) => order.indexOf(a.rarity) - order.indexOf(b.rarity)
  );

  for (const item of sorted) {
    const color = data.rarity?.[item.rarity]?.color ?? '#9d9d9d';
    const usable = !hero || canEquip(hero, item, data.classes);

    const cell = document.createElement('button');
    cell.className = 'pd-bag-cell';
    cell.dataset.rarity = item.rarity ?? 'common';
    cell.style.borderColor = color;
    cell.innerHTML = slotIconImg(item.slot, item, 28);
    cell.dataset.itemId = item.id;
    cell._pdItem = item;
    if (usable) {
      cell._pdAction = 'equipar';
      // Stash the upgrade/downgrade diff vs equipped so the tooltip can show it.
      if (hero) {
        const rows = equipDiffRows(hero, item, data).map(formatDiffRow);
        if (rows.length) cell.dataset.compare = JSON.stringify(rows);
      }
      cell.addEventListener('click', () => onEquip(item.id));
    } else {
      // Wrong class for this item — show it dimmed and locked, no equip action.
      cell.classList.add('locked');
      const need = weaponClassLabel(item, data);
      cell._pdLocked = `Não usável por ${data.classes?.[hero.classId]?.label ?? hero.classId}` + (need ? ` (somente ${need})` : '');
    }
    grid.appendChild(cell);
  }
  container.appendChild(grid);
}

/** Human-readable list of classes that can use a class-restricted item. */
function weaponClassLabel(item, data) {
  const cls = data.classes ?? {};
  let allowed = [];
  if (Array.isArray(item.classes) && item.classes.length) {
    allowed = item.classes;
  } else if (item.slot === 'weapon' && item.weapon) {
    allowed = Object.keys(cls).filter((id) => cls[id].weapon === item.weapon);
  }
  return allowed.map((id) => cls[id]?.label ?? id).join(', ');
}

// ---- Re-export from core for convenience ----
import { equipmentStatTotals } from '../../core/items.js';
import { canEquip } from '../../core/inventory.js';

/** Display labels + formatting for each stat in the compare panel. */
const STAT_META = {
  hp: ['Vida', false], maxHp: ['Vida', false], atk: ['Ataque', false], def: ['Defesa', false],
  mag: ['Magia', false], res: ['Res. Mág', false], atkSpd: ['Vel. Ataque', true],
  crit: ['Crít', true], critDmg: ['Dano Crít', true], dodge: ['Esquiva', true],
  lifesteal: ['Roubo de Vida', true], thorns: ['Espinhos', true],
  burn: ['Dano de Fogo', true], regen: ['Regen/turno', true],
  poison: ['Veneno', true],
};

/**
 * Stat-by-stat delta of a bag item versus what the hero has equipped in the
 * same slot — so the player sees at a glance whether it's an upgrade.
 * Rings compare against whichever ring is already worn. Returns rows like
 * { label:'Ataque', delta:+12, pct:false }.
 */
function equipDiffRows(hero, item, data) {
  if (!hero) return [];
  const slot = item.slot === 'ring'
    ? (hero.equipped.ring1 ? 'ring1' : (hero.equipped.ring2 ? 'ring2' : 'ring1'))
    : item.slot;
  const current = hero.equipped?.[slot] ?? null;
  const a = equipmentStatTotals(item);
  const b = current ? equipmentStatTotals(current) : {};
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const rows = [];
  for (const k of keys) {
    const meta = STAT_META[k];
    if (!meta) continue;
    const delta = (a[k] ?? 0) - (b[k] ?? 0);
    if (Math.abs(delta) < 1e-9) continue;
    rows.push({ label: meta[0], delta, pct: meta[1] });
  }
  // Bigger swings first.
  rows.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
  return rows;
}

/** Format a compare row delta as a signed, colour-ready string. */
export function formatDiffRow({ label, delta, pct }) {
  const sign = delta > 0 ? '+' : '−';
  const mag = pct ? `${Math.round(Math.abs(delta) * 100)}%` : `${Math.abs(delta)}`;
  return { text: `${sign}${mag} ${label}`, positive: delta > 0 };
}
